/**
 * AudioBars — a tiny live spectrum (vertical frequency bars) for the playback
 * bar, next to the time readout. Reads the Synth VR editor's shared analyser
 * (see audio/srAnalyser) and draws a few smoothed, accent-coloured bars. Sits
 * flat/low when paused (no signal), dances during playback.
 */

import React, { useEffect, useRef } from 'react';
import { getSrFrequencyData } from '../../audio/srAnalyser';
import { accentHex } from '../ui/themeColors';

const BAR_COUNT = 5;

function roundRectPath(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) {
  const rr = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + rr, y);
  ctx.arcTo(x + w, y, x + w, y + h, rr);
  ctx.arcTo(x + w, y + h, x, y + h, rr);
  ctx.arcTo(x, y + h, x, y, rr);
  ctx.arcTo(x, y, x + w, y, rr);
  ctx.closePath();
}

export default function AudioBars({ width = 34, height = 22, getData }: { width?: number; height?: number; getData?: () => Uint8Array | null }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const rafRef = useRef(0);
  const smoothRef = useRef<number[]>(new Array(BAR_COUNT).fill(0));
  // Frequency source: defaults to the Synth VR analyser; other editors pass
  // their own (Flow / Console) so the same spectrum widget works everywhere.
  const getDataRef = useRef(getData);
  getDataRef.current = getData;

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const dpr = window.devicePixelRatio || 1;
    canvas.width = Math.floor(width * dpr);
    canvas.height = Math.floor(height * dpr);
    canvas.style.width = width + 'px';
    canvas.style.height = height + 'px';
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    let colorTick = 0;
    let color = accentHex(0.74, 0.2);
    const gap = 2;
    const bw = (width - gap * (BAR_COUNT - 1)) / BAR_COUNT;

    const draw = () => {
      const data = (getDataRef.current ?? getSrFrequencyData)();
      if ((colorTick++ % 30) === 0) color = accentHex(0.74, 0.2); // follow live theme
      ctx.clearRect(0, 0, width, height);
      ctx.fillStyle = color;

      const smooth = smoothRef.current;
      for (let i = 0; i < BAR_COUNT; i++) {
        let v = 0;
        if (data) {
          // Average a slice of the low-mid spectrum (most musical energy).
          const lo = Math.floor((i / BAR_COUNT) * 220) + 2;
          const hi = Math.floor(((i + 1) / BAR_COUNT) * 220) + 2;
          let sum = 0;
          for (let b = lo; b < hi; b++) sum += data[b];
          v = sum / ((hi - lo) * 255); // 0..1
        }
        // Fast attack, slow release so the bars feel lively but not jittery.
        smooth[i] += (v - smooth[i]) * (v > smooth[i] ? 0.5 : 0.12);
        const minH = 2;
        const h = minH + Math.min(1, smooth[i] * 1.4) * (height - minH);
        roundRectPath(ctx, i * (bw + gap), height - h, bw, h, 1.5);
        ctx.fill();
      }
      rafRef.current = requestAnimationFrame(draw);
    };
    rafRef.current = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(rafRef.current);
  }, [width, height]);

  return <canvas ref={canvasRef} style={{ display: 'block', flexShrink: 0 }} />;
}
